import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  BookOpen, 
  Sparkles,
  Building2,
  CreditCard,
  Receipt,
  FileText,
  ArrowRight,
  Moon,
  Sun,
  Monitor,
  ChevronDown,
  CheckCircle2,
  Zap,
  BarChart3,
  TrendingUp,
  TrendingDown,
  Shield,
  Users,
  Globe,
  Star,
  Quote,
  Mail,
  Lock,
  User,
  Eye,
  EyeOff,
  X,
  Loader2,
  DollarSign,
  Landmark,
  Layers,
  Home,
  Check
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { cn } from '../lib/utils';

// ─── Animated Counter Component ─────────────────────────────────────────────
function AnimatedCounter({ end, duration = 1800, prefix = '', suffix = '', decimals = 0 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const startTime = performance.now();
          const step = (now) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
            setCount(eased * end);
            if (progress < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [end, duration]);

  const formatted = decimals > 0
    ? count.toFixed(decimals)
    : Math.round(count).toLocaleString();

  return <span ref={ref}>{prefix}{formatted}{suffix}</span>;
}

/* ─── QuickBooks Savings Calculator (Landing Page version) ─── */
const QB_PLANS_LANDING = [
  { name: 'Simple Start', price: 30 },
  { name: 'Essentials', price: 60 },
  { name: 'Plus', price: 90 },
  { name: 'Advanced', price: 200 },
];

const CB_PLANS_LANDING = [
  { name: 'Starter', price: 19.99, credits: 200, banks: 1 },
  { name: 'Pro', price: 49, credits: 1500, banks: 4, popular: true },
  { name: 'Business', price: 109, credits: 4000, banks: 10 },
];

function LandingSavingsCalculator({ onGetStarted }) {
  const [qbPlan, setQbPlan] = useState(2); // Default to Plus ($90)

  const qbMonthly = QB_PLANS_LANDING[qbPlan].price;

  return (
    <div className="space-y-6" data-testid="landing-savings-calculator">
      {/* QB Plan Selector */}
      <div className="flex flex-wrap justify-center gap-2">
        {QB_PLANS_LANDING.map((plan, i) => (
          <button
            key={plan.name}
            onClick={() => setQbPlan(i)}
            className={cn(
              'px-4 py-2.5 rounded-xl border text-sm transition-all',
              qbPlan === i
                ? 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 font-semibold shadow-sm'
                : 'border-border hover:border-muted-foreground/30 text-muted-foreground'
            )}
            data-testid={`landing-qb-plan-${i}`}
          >
            <span className="block text-[10px] uppercase tracking-wide opacity-70">QB {plan.name}</span>
            <span className="font-bold">${plan.price}/mo</span>
          </button>
        ))}
      </div>

      {/* Comparison Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {CB_PLANS_LANDING.map(plan => {
          const savings = qbMonthly - plan.price;
          const savingsPercent = Math.round((savings / qbMonthly) * 100);
          const yearlySavings = Math.round(savings * 12);
          return (
            <div
              key={plan.name}
              className={cn(
                'rounded-2xl border p-6 relative transition-all bg-card',
                plan.popular ? 'ring-2 ring-emerald-500/40 border-emerald-500 scale-[1.02]' : ''
              )}
              data-testid={`landing-comparison-${plan.name.toLowerCase()}`}
            >
              {plan.popular && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-xs px-3">Best Value</Badge>
              )}
              <p className="font-semibold text-lg">{plan.name}</p>
              <div className="flex items-baseline gap-1 mt-1">
                <span className="text-3xl font-bold text-emerald-600">${plan.price}</span>
                <span className="text-sm text-muted-foreground">/mo</span>
              </div>

              {savings > 0 && (
                <div className="mt-3 p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                  <div className="flex items-center gap-1.5">
                    <TrendingDown className="w-4 h-4 text-emerald-600" />
                    <span className="font-bold text-emerald-700 dark:text-emerald-400">
                      Save {savingsPercent}%
                    </span>
                  </div>
                  <p className="text-xs text-emerald-600 mt-1">
                    ${savings.toFixed(2)}/mo &middot; <span className="font-semibold">${yearlySavings}/yr</span>
                  </p>
                </div>
              )}

              <div className="mt-4 space-y-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-amber-500" /> {plan.credits} AI scans/month</div>
                <div className="flex items-center gap-2"><Landmark className="w-3.5 h-3.5 text-blue-500" /> {plan.banks} bank connections</div>
                <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-500" /> CFO Buck AI advisor</div>
                <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-500" /> OCR document scanner</div>
                <div className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-500" /> Tax return analysis</div>
              </div>

              <Button
                className={cn(
                  'w-full mt-5',
                  plan.popular
                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                    : 'bg-foreground hover:bg-foreground dark:hover:bg-muted text-background'
                )}
                onClick={() => onGetStarted?.('register')}
                data-testid={`landing-get-started-${plan.name.toLowerCase()}`}
              >
                Get Started <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          );
        })}
      </div>

      {/* Feature Comparison Table */}
      <div className="rounded-2xl border overflow-hidden bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50">
              <th className="text-left px-5 py-3 font-semibold">Feature</th>
              <th className="text-center px-5 py-3 font-semibold text-red-600">QuickBooks</th>
              <th className="text-center px-5 py-3 font-semibold text-emerald-600">CountBucks</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {[
              ['AI Financial Advisor (CFO Buck)', false, true],
              ['OCR Document Scanner', false, true],
              ['Smart Auto-Categorization', false, true],
              ['Tax Return Analysis & Playbook', false, true],
              ['Bank Connections via Plaid', true, true],
              ['Invoicing & Bills', true, true],
              ['Chart of Accounts', true, true],
              ['Financial Reports', true, true],
              ['Multi-company Support', '$90+/mo', 'All plans'],
              ['Starting Price', '$30/mo', '$19.99/mo'],
            ].map(([feature, qb, cb], i) => (
              <tr key={i} className="hover:bg-muted/20 transition-colors">
                <td className="px-5 py-3 text-foreground">{feature}</td>
                <td className="text-center px-5 py-3">
                  {typeof qb === 'boolean' ? (
                    qb ? <Check className="w-4 h-4 text-muted-foreground mx-auto" /> : <X className="w-4 h-4 text-red-400 mx-auto" />
                  ) : <span className="text-red-600 font-medium">{qb}</span>}
                </td>
                <td className="text-center px-5 py-3">
                  {typeof cb === 'boolean' ? (
                    cb ? <Check className="w-4 h-4 text-emerald-500 mx-auto" /> : <X className="w-4 h-4 text-red-400 mx-auto" />
                  ) : <span className="text-emerald-600 font-medium">{cb}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const { 
    user, 
    checkAuth, 
    loginWithGoogle, 
    loginWithEmail, 
    registerWithEmail, 
    verifyRegistration,
    verify2FALogin,
    resendVerificationCode,
    forgotPassword,
    resetPassword,
    loading: authLoading,
    authError,
    requires2FA,
    pendingEmail,
    clearAuthError
  } = useAuth();
  const { theme, setTheme, getThemeName } = useTheme();
  const navigate = useNavigate();
  const [activeFeature, setActiveFeature] = useState(0);
  
  // Auth modal state
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login', 'register', 'verify', '2fa', 'forgot', 'reset'
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    verificationCode: '',
    newPassword: ''
  });
  const [formError, setFormError] = useState('');

  useEffect(() => {
    const verify = async () => {
      const userData = await checkAuth();
      if (userData) {
        navigate('/dashboard', { replace: true });
      }
    };
    verify();
  }, [checkAuth, navigate]);

  // Auto-open auth modal when arriving from /login or /login?mode=register
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const mode = sp.get('mode');
    if (window.location.pathname === '/login' || mode) {
      setAuthMode(mode === 'register' ? 'register' : 'login');
      setShowAuthModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle 2FA requirement
  useEffect(() => {
    if (requires2FA && pendingEmail) {
      setAuthMode('2fa');
      setFormData(prev => ({ ...prev, email: pendingEmail }));
    }
  }, [requires2FA, pendingEmail]);

  const resetForm = () => {
    setFormData({ email: '', password: '', name: '', verificationCode: '', newPassword: '' });
    setFormError('');
    setShowPassword(false);
    clearAuthError();
  };

  const handleOpenAuthModal = (mode = 'login') => {
    resetForm();
    setAuthMode(mode);
    setShowAuthModal(true);
  };

  const handleCloseAuthModal = () => {
    setShowAuthModal(false);
    resetForm();
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setFormError('');
    
    if (!formData.email || !formData.password) {
      setFormError('Please fill in all fields');
      return;
    }
    
    const result = await loginWithEmail(formData.email, formData.password);
    
    if (result.success) {
      if (result.requires2FA) {
        setAuthMode('2fa');
      } else {
        handleCloseAuthModal();
        navigate('/dashboard');
      }
    } else {
      setFormError(result.error);
    }
  };

  // State for dev code when email fails
  const [devCode, setDevCode] = useState('');

  const handleEmailRegister = async (e) => {
    e.preventDefault();
    setFormError('');
    setDevCode('');
    
    if (!formData.email || !formData.password || !formData.name) {
      setFormError('Please fill in all fields');
      return;
    }
    
    if (formData.password.length < 8) {
      setFormError('Password must be at least 8 characters');
      return;
    }
    
    const result = await registerWithEmail(formData.email, formData.password, formData.name);
    
    if (result.success) {
      // If email failed, store the dev code
      if (result.dev_code) {
        setDevCode(result.dev_code);
      }
      setAuthMode('verify');
    } else {
      setFormError(result.error);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setFormError('');
    
    if (!formData.verificationCode || formData.verificationCode.length !== 6) {
      setFormError('Please enter a valid 6-digit code');
      return;
    }
    
    const result = await verifyRegistration(formData.email, formData.verificationCode);
    
    if (result.success) {
      handleCloseAuthModal();
      navigate('/dashboard');
    } else {
      setFormError(result.error);
    }
  };

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    setFormError('');
    
    if (!formData.verificationCode || formData.verificationCode.length !== 6) {
      setFormError('Please enter a valid 6-digit code');
      return;
    }
    
    const result = await verify2FALogin(formData.verificationCode);
    
    if (result.success) {
      handleCloseAuthModal();
      navigate('/dashboard');
    } else {
      setFormError(result.error);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setFormError('');
    setDevCode('');
    
    if (!formData.email) {
      setFormError('Please enter your email address');
      return;
    }
    
    const result = await forgotPassword(formData.email);
    
    if (result.success) {
      // If email failed, store the dev code
      if (result.dev_code) {
        setDevCode(result.dev_code);
      }
      setAuthMode('reset');
    } else {
      setFormError(result.error);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setFormError('');
    
    if (!formData.verificationCode || formData.verificationCode.length !== 6) {
      setFormError('Please enter a valid 6-digit code');
      return;
    }
    
    if (!formData.newPassword || formData.newPassword.length < 8) {
      setFormError('Password must be at least 8 characters');
      return;
    }
    
    const result = await resetPassword(formData.email, formData.verificationCode, formData.newPassword);
    
    if (result.success) {
      handleCloseAuthModal();
      navigate('/dashboard');
    } else {
      setFormError(result.error);
    }
  };

  const handleResendCode = async () => {
    const result = await resendVerificationCode(formData.email);
    if (result.success) {
      setFormError('');
      // Show success message briefly
      setFormError('New code sent! Check your email.');
      setTimeout(() => setFormError(''), 3000);
    } else {
      setFormError(result.error);
    }
  };

  const handleResendForgotCode = async () => {
    const result = await forgotPassword(formData.email);
    if (result.success) {
      setFormError('New code sent! Check your email.');
      setTimeout(() => setFormError(''), 3000);
    } else {
      setFormError(result.error);
    }
  };

  const differentiators = [
    {
      icon: Sparkles,
      title: 'CFO Buck AI Advisor',
      description: 'Your always-on AI financial advisor. Ask anything about cash flow, taxes, or strategy — get answers grounded in your real data.',
      color: 'violet'
    },
    {
      icon: FileText,
      title: 'AI Document Scanner',
      description: 'OCR-powered scanning for tax returns, invoices, and financial documents. Extracts key data and analyzes with AI.',
      color: 'blue'
    },
    {
      icon: BarChart3,
      title: 'DSCR Loan Qualification',
      description: 'Know your loan readiness instantly. Real-time debt service coverage analysis with AI-powered action plans.',
      color: 'green'
    },
    {
      icon: TrendingUp,
      title: 'Tax Return Analysis',
      description: 'Upload returns and get AI-driven optimization strategies — missed deductions, credits, and a year-ahead tax plan.',
      color: 'emerald'
    }
  ];

  const features = [
    {
      id: 'cfo-buck',
      title: 'CFO Buck AI Chat',
      description: 'A persistent AI financial advisor available from any page. Ask about cash flow, profitability, taxes — grounded in your actual financial data.',
      icon: Sparkles,
      highlights: ['Floating chat widget on every page', 'Full-page chat history', 'Context-aware financial answers', 'Per-session credit billing']
    },
    {
      id: 'scanner',
      title: 'Document Scanner & Tax Analysis',
      description: 'OCR-powered scanning for tax returns, invoices, receipts, and financial documents. AI extracts key data and generates optimization reports.',
      icon: FileText,
      highlights: ['Multi-page parallel OCR', 'Tax return analysis with AI playbook', 'Per-form detail extraction', 'Scan credit billing']
    },
    {
      id: 'migration',
      title: 'Switch from QuickBooks',
      description: 'Seamless migration hub with CSV import, field mapping, and savings calculator. Know exactly how much you\'ll save before you switch.',
      icon: ArrowRight,
      highlights: ['CSV import wizard', 'Automatic field mapping', 'Real-time savings comparison', 'Zero data loss guarantee']
    },
    {
      id: 'banking',
      title: 'Banking & AI Categorization',
      description: 'Connect bank accounts via Plaid, auto-sync transactions, and let AI Buck categorize everything with 95%+ accuracy. Reconcile with AI assistance.',
      icon: CreditCard,
      highlights: ['Plaid bank connections', 'AI auto-categorization (95%+)', 'Smart Reconcile AI matching', 'Statement upload & reconciliation']
    }
  ];

  const stats = [
    { end: 10000, suffix: '+', label: 'Transactions Processed', decimals: 0 },
    { end: 95, suffix: '%', label: 'AI Accuracy', decimals: 0 },
    { text: '< 1 min', label: 'Bank Sync Time' },
    { end: 99.9, suffix: '%', label: 'Uptime', decimals: 1 }
  ];

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0f1729] backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src="/countbucks-logo.png" alt="CountBucks" className="w-10 h-10" />
              <span className="font-bold text-xl text-emerald-400">
                CountBucks
              </span>
            </div>
            
            <div className="flex items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    data-testid="theme-toggle"
                    aria-label="Change theme"
                  >
                    {theme === 'dark' ? (
                      <Moon className="w-5 h-5" />
                    ) : theme === 'midnight' ? (
                      <Sparkles className="w-5 h-5" />
                    ) : theme === 'classic' ? (
                      <Monitor className="w-5 h-5" />
                    ) : (
                      <Sun className="w-5 h-5" />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem 
                    onClick={() => setTheme('light')}
                    className={cn("cursor-pointer gap-2", theme === 'light' && "bg-muted")}
                  >
                    <Sun className="w-4 h-4" />
                    Light
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => setTheme('dark')}
                    className={cn("cursor-pointer gap-2", theme === 'dark' && "bg-muted")}
                  >
                    <Moon className="w-4 h-4" />
                    Dark
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => setTheme('midnight')}
                    className={cn("cursor-pointer gap-2", theme === 'midnight' && "bg-muted")}
                  >
                    <Sparkles className="w-4 h-4" />
                    Midnight
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => setTheme('classic')}
                    className={cn("cursor-pointer gap-2", theme === 'classic' && "bg-muted")}
                  >
                    <Monitor className="w-4 h-4" />
                    Classic
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              <Button 
                onClick={() => handleOpenAuthModal('login')}
                data-testid="login-button"
                className="gap-2 bg-gradient-to-r from-emerald-500 to-blue-500 hover:from-emerald-600 hover:to-blue-600 text-white border-0"
              >
                Sign In
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Dark gradient background */}
      <section className="relative pt-24 pb-20 px-6 lg:px-12 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950" />
        
        {/* Animated gradient orbs */}
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-violet-500/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
        
        <div className="relative max-w-7xl mx-auto">
          <div className="flex flex-col items-center text-center min-h-[65vh] justify-center">
            {/* Badge Row */}
            <div className="flex flex-col items-center gap-1.5 mb-8">
              <Badge 
                className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 px-4 py-1.5 text-sm"
                data-testid="ai-buck-badge"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                AI-Powered Platform
              </Badge>
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] text-white/40 uppercase tracking-wider">Powered by</span>
                <span className="text-[11px] font-semibold text-[#10a37f] tracking-wide">ChatGPT · OpenAI</span>
                <span className="text-[11px] text-white/30">&amp;</span>
                <span className="text-[11px] font-semibold text-[#f59e0b] tracking-wide">Claude · Anthropic</span>
              </div>
            </div>
            
            {/* Headline */}
            <p className="text-sm uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-4">Online Accounting &amp; Bookkeeping</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-white max-w-4xl">
              Your AI-Powered CFO.
              <br />
              <span className="bg-gradient-to-r from-emerald-400 via-blue-400 to-violet-400 bg-clip-text text-transparent">
                Accounting. Planning. Taxes.
              </span>
              <br />
              <span className="text-white/90">All in One.</span>
            </h1>
            
            <p className="text-base sm:text-lg text-white/60 max-w-2xl mt-6 leading-relaxed">
              Instant financial advice, document scanning, missing deduction identification, and loan qualification — all powered by AI that knows your numbers.
            </p>
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <Button 
                size="lg" 
                onClick={() => handleOpenAuthModal('register')}
                data-testid="get-started-btn"
                className="gap-2 text-base bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white border-0 shadow-lg shadow-emerald-500/25 h-12 px-8"
              >
                Choose Starter — $19.99/mo
                <ArrowRight className="w-5 h-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => {
                  document.getElementById('savings')?.scrollIntoView({ behavior: 'smooth' });
                }}
                data-testid="compare-plans-btn"
                className="text-base border-2 border-emerald-500/50 text-white hover:bg-emerald-500/20 hover:border-emerald-400 h-12 px-8 gap-2 transition-all"
              >
                <DollarSign className="w-4 h-4" />
                Compare Plans
              </Button>
            </div>
            
            {/* Quick stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-10 pt-10 mt-10 border-t border-white/10 w-full max-w-3xl text-white">
              {stats.map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl font-bold">
                    {stat.text ? stat.text : (
                      <AnimatedCounter end={stat.end} suffix={stat.suffix} decimals={stat.decimals} />
                    )}
                  </div>
                  <div className="text-xs text-white/50 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Why CountBucks Section ─── */}
      <section className="py-16 px-6 lg:px-12 bg-background border-b border-border">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-lg font-semibold text-center text-muted-foreground mb-10">Why businesses choose CountBucks</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-card border border-border rounded-2xl p-6 space-y-3">
              <div className="w-11 h-11 rounded-xl bg-emerald-500/15 flex items-center justify-center">
                <Shield className="w-6 h-6 text-emerald-500" />
              </div>
              <h3 className="text-base font-bold text-foreground tracking-tight">Built by a CPA. 50+ Years of Experience.</h3>
              <p className="text-sm text-muted-foreground leading-relaxed font-medium">
                We took decades of certified public accounting expertise and identified exactly what business owners and individuals need to take control of their finances — then built it.
              </p>
            </div>
            <div className="bg-card border border-border rounded-2xl p-6 space-y-3">
              <div className="w-11 h-11 rounded-xl bg-blue-500/15 flex items-center justify-center">
                <ArrowRight className="w-6 h-6 text-blue-500" />
              </div>
              <h3 className="text-base font-bold text-foreground tracking-tight">Switching Is Simple. We Walk You Through It.</h3>
              <p className="text-sm text-muted-foreground leading-relaxed font-medium">
                Transfer all your data from QuickBooks or any accounting software in just a few clicks. Our guided import wizard maps your transactions automatically — no data loss.
              </p>
            </div>
            <div className="bg-card border border-border rounded-2xl p-6 space-y-3">
              <div className="w-11 h-11 rounded-xl bg-cyan-500/15 flex items-center justify-center">
                <Landmark className="w-6 h-6 text-cyan-500" />
              </div>
              <h3 className="text-base font-bold text-foreground tracking-tight">Live Banking Feeds. Secured by Plaid.</h3>
              <p className="text-sm text-muted-foreground leading-relaxed font-medium">
                Your bank transactions flow in automatically through Plaid's bank-grade encrypted connection. Real-time visibility into every dollar — no manual entry, no delays.
              </p>
            </div>
            <div className="bg-card border border-border rounded-2xl p-6 space-y-3">
              <div className="w-11 h-11 rounded-xl bg-violet-500/15 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-violet-500" />
              </div>
              <h3 className="text-base font-bold text-foreground tracking-tight">AI That Actually Knows Your Numbers.</h3>
              <p className="text-sm text-muted-foreground leading-relaxed font-medium">
                CFO Buck isn't a generic chatbot. It reads your real financial data — cash flow, expenses, tax returns — and gives answers specific to your business, instantly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Feature Showcase Grid ─── */}
      <section className="py-16 px-6 lg:px-12 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-lg font-semibold text-center text-muted-foreground mb-10">Everything you need in one platform</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: FileText, label: 'Tax Return Scanning', desc: 'OCR extracts every line, AI finds missed deductions', color: 'emerald' },
              { icon: Sparkles, label: 'CFO Buck Advisor', desc: 'Ask any financial question and get an instant AI answer — cash flow, taxes, strategy, anything', color: 'violet' },
              { icon: BarChart3, label: 'Loan Qualification', desc: 'DSCR analysis with AI-powered action plans', color: 'blue' },
              { icon: CreditCard, label: 'Live Bank Feeds via Plaid', desc: 'Transactions sync automatically — real-time tracking with bank-grade encryption', color: 'cyan' },
              { icon: Receipt, label: 'Invoice & Bill Scanner', desc: 'Drop invoices, receipts — data extracted instantly', color: 'amber' },
              { icon: TrendingUp, label: 'Personal Budgeting', desc: 'Household financial overview alongside business', color: 'pink' },
            ].map((feat, i) => (
              <div
                key={i}
                className="bg-card border border-border rounded-xl p-5 hover:border-emerald-500/30 transition-colors"
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${
                  feat.color === 'emerald' ? 'bg-emerald-500/15' :
                  feat.color === 'violet' ? 'bg-violet-500/15' :
                  feat.color === 'blue' ? 'bg-blue-500/15' :
                  feat.color === 'cyan' ? 'bg-cyan-500/15' :
                  feat.color === 'amber' ? 'bg-amber-500/15' :
                  'bg-pink-500/15'
                }`}>
                  <feat.icon className={`w-5 h-5 ${
                    feat.color === 'emerald' ? 'text-emerald-500' :
                    feat.color === 'violet' ? 'text-violet-500' :
                    feat.color === 'blue' ? 'text-blue-500' :
                    feat.color === 'cyan' ? 'text-cyan-500' :
                    feat.color === 'amber' ? 'text-amber-500' :
                    'text-pink-500'
                  }`} />
                </div>
                <p className="text-sm font-bold text-foreground mb-1 tracking-tight">{feat.label}</p>
                <p className="text-xs text-muted-foreground leading-relaxed font-medium">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted By Thousands Section */}
      <section className="py-12 px-6 lg:px-12 bg-muted/50 border-y border-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16">
            <div className="flex items-center gap-3">
              <div className="flex -space-x-3">
                {[
                  'https://images.unsplash.com/photo-1609436132311-e4b0c9370469?w=80&h=80&fit=crop&crop=face',
                  'https://images.unsplash.com/photo-1714976326861-dd180a8a57b9?w=80&h=80&fit=crop&crop=face',
                  'https://images.unsplash.com/photo-1609371497456-3a55a205d5eb?w=80&h=80&fit=crop&crop=face',
                  'https://images.unsplash.com/photo-1753164726626-e4c38056a03f?w=80&h=80&fit=crop&crop=face',
                  'https://images.unsplash.com/photo-1769636929130-56648d6e9c6d?w=80&h=80&fit=crop&crop=face',
                ].map((src, i) => (
                  <img 
                    key={i}
                    src={src}
                    alt=""
                    className="w-10 h-10 rounded-full border-2 border-white dark:border-border object-cover"
                  />
                ))}
              </div>
              <div>
                <p className="font-semibold text-foreground">Trusted by 5,000+ Users</p>
                <p className="text-sm text-muted-foreground">Businesses & accountants worldwide</p>
              </div>
            </div>
            
            <div className="h-12 w-px bg-border hidden md:block" />
            
            <div className="flex items-center gap-2">
              <div className="flex">
                {[1,2,3,4,5].map((i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <div>
                <p className="font-semibold text-foreground">4.9/5 Rating</p>
                <p className="text-sm text-muted-foreground">From 500+ reviews</p>
              </div>
            </div>
            
            <div className="h-12 w-px bg-border hidden md:block" />
            
            <div className="flex items-center gap-3">
              <Shield className="w-10 h-10 text-emerald-500" />
              <div>
                <p className="font-semibold text-foreground">Bank-Level Security</p>
                <p className="text-sm text-muted-foreground">256-bit encryption</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Differentiators Section */}
      <section className="py-20 px-6 lg:px-12 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 mb-4">
              Why Choose CountBucks
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              Built Different. Built Better.
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Modern accounting software that combines the rigor of double-entry bookkeeping with the power of artificial intelligence.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {differentiators.map((item, i) => (
              <div 
                key={i}
                className={cn(
                  "group relative bg-card rounded-2xl p-6 border border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl hover:-translate-y-1",
                  i === 0 && "lg:col-span-1"
                )}
                data-testid={`differentiator-${i}`}
              >
                {/* Gradient glow on hover */}
                <div className={cn(
                  "absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity -z-10 blur-xl",
                  item.color === 'violet' && "bg-violet-500/10",
                  item.color === 'blue' && "bg-blue-500/10",
                  item.color === 'green' && "bg-emerald-500/10",
                  item.color === 'emerald' && "bg-teal-500/10"
                )} />
                
                <div className={cn(
                  "w-14 h-14 rounded-xl flex items-center justify-center mb-5",
                  item.color === 'violet' && "bg-violet-500/10",
                  item.color === 'blue' && "bg-blue-500/10",
                  item.color === 'green' && "bg-emerald-500/10",
                  item.color === 'emerald' && "bg-teal-500/10"
                )}>
                  <item.icon className={cn(
                    "w-7 h-7",
                    item.color === 'violet' && "text-violet-500",
                    item.color === 'blue' && "text-blue-500",
                    item.color === 'green' && "text-emerald-500",
                    item.color === 'emerald' && "text-teal-500"
                  )} />
                </div>
                
                <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Global Financial Snapshot Section ─── */}
      <section className="py-20 px-6 lg:px-12 bg-gradient-to-b from-background to-muted/30 border-b border-border">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left — Copy */}
            <div className="space-y-6">
              <Badge className="bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20" data-testid="snapshot-badge">
                Multi-Entity Dashboard
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
                One Login.<br />Every Dollar.<br />
                <span className="bg-gradient-to-r from-emerald-400 to-blue-400 bg-clip-text text-transparent">Business & Personal.</span>
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Create separate workspaces for each business, side hustle, or personal household — then see it all in one place. A true global financial snapshot that gives you the complete picture, from your LLC's P&L to your family budget.
              </p>
              <ul className="space-y-3">
                {[
                  'Unlimited workspaces — business or personal',
                  'Switch between entities with one click',
                  'Separate books, shared AI intelligence',
                  'See your full net worth across everything'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <Button 
                onClick={() => handleOpenAuthModal('register')}
                className="gap-2 bg-gradient-to-r from-violet-500 to-blue-500 hover:from-violet-600 hover:to-blue-600 text-white border-0 mt-2"
                data-testid="snapshot-cta"
              >
                Get Started Free
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Right — Visual mockup */}
            <div className="relative" data-testid="snapshot-visual">
              <div className="bg-card rounded-2xl border border-border p-6 shadow-xl space-y-5">
                {/* Header */}
                <div className="flex items-center gap-3 pb-4 border-b border-border">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center">
                    <Layers className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold">Global Financial Snapshot</div>
                    <div className="text-xs text-muted-foreground">Across all workspaces</div>
                  </div>
                </div>

                {/* Workspace cards */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 rounded-xl bg-muted/50 border border-border">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                        <Building2 className="w-4 h-4 text-emerald-500" />
                      </div>
                      <div>
                        <div className="text-sm font-medium">Smith CPA Firm</div>
                        <div className="text-xs text-muted-foreground">Business</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-emerald-500">+$124,800</div>
                      <div className="text-xs text-muted-foreground">Net Income</div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-xl bg-muted/50 border border-border">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                        <Building2 className="w-4 h-4 text-blue-500" />
                      </div>
                      <div>
                        <div className="text-sm font-medium">Rental Properties LLC</div>
                        <div className="text-xs text-muted-foreground">Business</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-emerald-500">+$38,400</div>
                      <div className="text-xs text-muted-foreground">Net Income</div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-xl bg-muted/50 border border-border">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center">
                        <Home className="w-4 h-4 text-violet-500" />
                      </div>
                      <div>
                        <div className="text-sm font-medium">Smith Household</div>
                        <div className="text-xs text-muted-foreground">Personal</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-blue-500">$892,500</div>
                      <div className="text-xs text-muted-foreground">Net Worth</div>
                    </div>
                  </div>
                </div>

                {/* Totals bar */}
                <div className="pt-4 border-t border-border">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-lg font-bold text-emerald-500">$163K</div>
                      <div className="text-xs text-muted-foreground">Total Income</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-foreground">$892K</div>
                      <div className="text-xs text-muted-foreground">Net Worth</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-blue-500">3</div>
                      <div className="text-xs text-muted-foreground">Entities</div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Decorative glow */}
              <div className="absolute -inset-4 bg-gradient-to-r from-violet-500/5 via-blue-500/5 to-emerald-500/5 rounded-3xl -z-10 blur-xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Interactive Tabs */}
      <section className="py-20 px-6 lg:px-12 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20 mb-4">
              Platform Features
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              Everything You Need
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              From daily bookkeeping to board-ready financial reports, CountBucks has you covered.
            </p>
          </div>
          
          {/* Feature tabs */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {features.map((feature, i) => (
              <button
                key={feature.id}
                onClick={() => setActiveFeature(i)}
                className={cn(
                  "flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all",
                  activeFeature === i 
                    ? "bg-gradient-to-r from-emerald-500 to-blue-500 text-white shadow-lg" 
                    : "bg-card border border-border hover:border-primary/50 text-muted-foreground hover:text-foreground"
                )}
                data-testid={`feature-tab-${feature.id}`}
              >
                <feature.icon className="w-4 h-4" />
                {feature.title}
              </button>
            ))}
          </div>
          
          {/* Feature content */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium">
                {(() => {
                  const Icon = features[activeFeature].icon;
                  return <Icon className="w-4 h-4" />;
                })()}
                {features[activeFeature].title}
              </div>
              
              <h3 className="text-2xl sm:text-3xl font-bold">
                {features[activeFeature].title}
              </h3>
              
              <p className="text-muted-foreground text-lg leading-relaxed">
                {features[activeFeature].description}
              </p>
              
              <ul className="space-y-3">
                {features[activeFeature].highlights.map((highlight, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    <span className="text-sm">{highlight}</span>
                  </li>
                ))}
              </ul>
              
              <Button 
                onClick={() => handleOpenAuthModal('register')}
                className="gap-2 bg-gradient-to-r from-emerald-500 to-blue-500 hover:from-emerald-600 hover:to-blue-600 text-white border-0 mt-4"
                data-testid="feature-cta"
              >
                Try It Free
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Feature visual */}
            <div className="relative">
              <div className="bg-card rounded-2xl border border-border p-6 shadow-xl">
                {activeFeature === 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-emerald-500" />
                      </div>
                      <div>
                        <div className="font-medium">CFO Buck</div>
                        <div className="text-xs text-muted-foreground">AI Financial Advisor</div>
                      </div>
                      <Badge className="ml-auto bg-emerald-500/20 text-emerald-600 text-xs">Online</Badge>
                    </div>
                    <div className="bg-muted/50 rounded-xl p-3 text-sm">
                      <p className="text-muted-foreground text-xs mb-1">You asked:</p>
                      <p>"How's my cash flow looking this quarter?"</p>
                    </div>
                    <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 text-sm">
                      <p className="text-emerald-600 dark:text-emerald-400 text-xs font-medium mb-1">CFO Buck</p>
                      <p className="text-sm">Your Q4 cash flow is strong at +$42K. Revenue is up 23% vs Q3. I'd recommend setting aside 15% for estimated taxes before year-end.</p>
                    </div>
                    <div className="flex gap-2">
                      {["Tax strategy", "Expense breakdown", "Loan readiness"].map(q => (
                        <span key={q} className="text-[11px] px-2.5 py-1 rounded-full border border-border text-muted-foreground">{q}</span>
                      ))}
                    </div>
                  </div>
                )}
                {activeFeature === 1 && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Document Scanner</span>
                      <Badge className="bg-blue-500/20 text-blue-600 text-xs">AI-Powered</Badge>
                    </div>
                    {[
                      { name: '2024 Tax Return (1040)', pages: 12, status: 'Analyzed' },
                      { name: 'Q3 Invoice Batch', pages: 8, status: 'Scanned' },
                      { name: 'W-2 Forms', pages: 3, status: 'Extracted' },
                    ].map((doc, i) => (
                      <div key={i} className="flex items-center justify-between bg-muted/50 rounded-lg p-3">
                        <div className="flex items-center gap-3">
                          <FileText className="w-4 h-4 text-blue-500" />
                          <div>
                            <span className="text-sm font-medium">{doc.name}</span>
                            <span className="text-xs text-muted-foreground block">{doc.pages} pages</span>
                          </div>
                        </div>
                        <Badge variant="outline" className="text-xs text-emerald-500 border-emerald-500/30">{doc.status}</Badge>
                      </div>
                    ))}
                    <div className="bg-violet-500/10 border border-violet-500/20 rounded-xl p-3">
                      <p className="text-xs font-medium text-violet-600 dark:text-violet-400">AI Analysis Found</p>
                      <p className="text-xs text-muted-foreground mt-1">3 missed deductions worth ~$4,200. View full report.</p>
                    </div>
                  </div>
                )}
                {activeFeature === 2 && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                      <ArrowRight className="w-5 h-5 text-emerald-500" />
                      <span className="font-medium">QuickBooks Migration</span>
                    </div>
                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-red-600">QuickBooks Plus</p>
                        <p className="text-xs text-muted-foreground">$90/month</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium text-emerald-600">CountBucks Pro</p>
                        <p className="text-xs text-muted-foreground">$49/month</p>
                      </div>
                    </div>
                    <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 text-center">
                      <p className="text-lg font-bold text-emerald-600">Save $372/year</p>
                      <p className="text-xs text-muted-foreground">35% less than QuickBooks</p>
                    </div>
                    {['Chart of Accounts', 'Transaction History', 'Customer/Vendor Lists'].map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        <span>{item} imported</span>
                      </div>
                    ))}
                  </div>
                )}
                {activeFeature === 3 && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Bank Accounts</span>
                      <Badge variant="outline" className="text-xs text-emerald-500 border-emerald-500/30">Connected</Badge>
                    </div>
                    {['Chase Business ****4521', 'Wells Fargo ****8832', 'Mercury ****1209'].map((account, i) => (
                      <div key={i} className="flex items-center justify-between bg-muted/50 rounded-lg p-3">
                        <div className="flex items-center gap-3">
                          <CreditCard className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm">{account}</span>
                        </div>
                        <span className="text-sm font-mono text-emerald-500">${[48, 23, 67][i]}K</span>
                      </div>
                    ))}
                    <div className="bg-violet-500/10 border border-violet-500/20 rounded-xl p-3 flex items-center gap-3">
                      <Sparkles className="w-5 h-5 text-violet-500 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-medium text-violet-600 dark:text-violet-400">AI Buck categorized 47 transactions</p>
                        <p className="text-xs text-muted-foreground">95% confidence — ready for review</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 px-6 lg:px-12 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-7 h-7 text-emerald-500" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Bank-Grade Security</h3>
              <p className="text-sm text-muted-foreground">
                SOC 2 compliant, 256-bit encryption, and secure Plaid integration for bank connections.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-14 h-14 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto mb-4">
                <Users className="w-7 h-7 text-blue-500" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Built for Teams</h3>
              <p className="text-sm text-muted-foreground">
                Role-based access control with 6 permission levels from Owner to Client Portal.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-14 h-14 rounded-full bg-violet-500/10 flex items-center justify-center mx-auto mb-4">
                <Globe className="w-7 h-7 text-violet-500" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Always Available</h3>
              <p className="text-sm text-muted-foreground">
                99.9% uptime SLA with automatic backups and disaster recovery built in.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-6 lg:px-12 bg-muted/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20 mb-4">
              Customer Stories
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              Loved by Thousands of Businesses
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              See why business owners and accountants trust CountBucks for their financial management.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="bg-card rounded-2xl p-8 border border-border relative">
              <Quote className="absolute top-6 right-6 w-8 h-8 text-emerald-500/20" />
              <div className="flex items-center gap-1 mb-4">
                {[1,2,3,4,5].map((i) => (
                  <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <p className="text-foreground mb-6 leading-relaxed">
                "CountBucks has completely transformed how I manage my clients' books. The CFO Buck AI saves me hours analyzing tax returns, and the OCR scanner handles all my document processing automatically."
              </p>
              <div className="flex items-center gap-3">
                <img 
                  src="https://images.unsplash.com/photo-1573497491207-618cc224f243?w=100&h=100&fit=crop&crop=face"
                  alt="Sarah Mitchell"
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <p className="font-semibold text-foreground">Sarah Mitchell</p>
                  <p className="text-sm text-muted-foreground">CPA, Mitchell & Associates</p>
                </div>
              </div>
            </div>
            
            {/* Testimonial 2 */}
            <div className="bg-card rounded-2xl p-8 border border-border relative">
              <Quote className="absolute top-6 right-6 w-8 h-8 text-blue-500/20" />
              <div className="flex items-center gap-1 mb-4">
                {[1,2,3,4,5].map((i) => (
                  <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <p className="text-foreground mb-6 leading-relaxed">
                "As a small business owner, I never thought accounting could be this simple. CFO Buck answers my financial questions instantly, and the DSCR calculator helped me secure my business loan."
              </p>
              <div className="flex items-center gap-3">
                <img 
                  src="https://images.unsplash.com/photo-1622626426572-c268eb006092?w=100&h=100&fit=crop&crop=face"
                  alt="James Rodriguez"
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <p className="font-semibold text-foreground">James Rodriguez</p>
                  <p className="text-sm text-muted-foreground">Owner, TechStart Solutions</p>
                </div>
              </div>
            </div>
            
            {/* Testimonial 3 */}
            <div className="bg-card rounded-2xl p-8 border border-border relative">
              <Quote className="absolute top-6 right-6 w-8 h-8 text-violet-500/20" />
              <div className="flex items-center gap-1 mb-4">
                {[1,2,3,4,5].map((i) => (
                  <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <p className="text-foreground mb-6 leading-relaxed">
                "We switched from QuickBooks and saved over $800 a year. The migration was seamless, AI Buck categorizes everything automatically, and the tax return analysis found deductions we missed."
              </p>
              <div className="flex items-center gap-3">
                <img 
                  src="https://images.unsplash.com/photo-1770058428154-9eee8a6a1fbb?w=100&h=100&fit=crop&crop=face"
                  alt="Emily Watson"
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <p className="font-semibold text-foreground">Emily Watson</p>
                  <p className="text-sm text-muted-foreground">CFO, GrowthHub Agency</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QuickBooks Savings Calculator Section */}
      <section className="py-20 px-6 lg:px-12 bg-background" id="savings" data-testid="landing-savings-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800">
              <DollarSign className="w-3 h-3 mr-1" />
              Switching from QuickBooks?
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-3">
              See How Much You'll Save
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Compare your current QuickBooks plan to CountBucks. AI-powered features at a fraction of the price.
            </p>
          </div>

          <LandingSavingsCalculator onGetStarted={handleOpenAuthModal} />
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 rounded-3xl p-12 relative overflow-hidden">
            {/* Background gradient orbs */}
            <div className="absolute top-0 left-1/4 w-64 h-64 bg-emerald-500/20 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl" />
            
            <div className="relative space-y-6">
              <Badge className="bg-white/10 text-white border-white/20">
                <Zap className="w-3 h-3 mr-1" />
                Start in 60 seconds
              </Badge>
              
              <h2 className="text-3xl sm:text-4xl font-bold text-white">
                Ready to Transform Your Accounting?
              </h2>
              
              <p className="text-muted-foreground/50 max-w-xl mx-auto">
                Join thousands of businesses using AI-powered accounting to save time and make better financial decisions.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <Button 
                  size="lg"
                  onClick={() => handleOpenAuthModal('register')}
                  className="gap-2 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white border-0 shadow-lg h-12 px-8"
                  data-testid="final-cta-btn"
                >
                  Start Free Trial
                  <ArrowRight className="w-5 h-5" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => {
                    document.getElementById('savings')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="border-white/20 text-white hover:bg-foreground h-12 px-8 gap-2"
                >
                  <DollarSign className="w-4 h-4" />
                  Compare Plans
                </Button>
              </div>
              
              <p className="text-xs text-muted-foreground pt-4">
                7 days free, then $19.99/mo • Cancel before day 8 and pay $0
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 lg:px-12 border-t border-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src="/countbucks-logo.png" alt="CountBucks" className="w-8 h-8" />
              <span className="font-bold text-lg bg-gradient-to-r from-emerald-400 to-green-300 bg-clip-text text-transparent">
                CountBucks
              </span>
            </div>
            
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <a href="#" className="hover:text-foreground transition-colors">Privacy</a>
              <a href="#" className="hover:text-foreground transition-colors">Terms</a>
              <a href="#" className="hover:text-foreground transition-colors">Security</a>
              <a href="#" className="hover:text-foreground transition-colors">Contact</a>
            </div>
            
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} CountBucks. AI-Powered Accounting.
            </p>
          </div>
        </div>
      </footer>

      {/* Auth Modal - CountBucks Themed */}
      <Dialog open={showAuthModal} onOpenChange={setShowAuthModal}>
        <DialogContent className="sm:max-w-[440px] p-0 overflow-hidden border-0 bg-transparent" data-testid="auth-modal">
          <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl overflow-hidden">
            {/* Header with Logo */}
            <div className="relative px-8 pt-8 pb-6 text-center">
              {/* Background glow effects */}
              <div className="absolute top-0 left-1/4 w-32 h-32 bg-emerald-500/20 rounded-full blur-3xl" />
              <div className="absolute top-0 right-1/4 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl" />
              
              <div className="relative">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <img src="/countbucks-logo.png" alt="CountBucks" className="w-12 h-12" />
                  <span className="font-bold text-2xl bg-gradient-to-r from-emerald-400 to-green-300 bg-clip-text text-transparent">
                    CountBucks
                  </span>
                </div>
                <h2 className="text-xl font-semibold text-white mb-1">
                  {authMode === 'login' && 'Welcome Back'}
                  {authMode === 'register' && 'Create Your Account'}
                  {authMode === 'verify' && 'Verify Your Email'}
                  {authMode === '2fa' && 'Security Verification'}
                  {authMode === 'forgot' && 'Reset Password'}
                  {authMode === 'reset' && 'Create New Password'}
                </h2>
                <p className="text-muted-foreground text-sm">
                  {authMode === 'login' && 'Sign in to access your financial dashboard'}
                  {authMode === 'register' && 'Start your free 14-day trial today'}
                  {authMode === 'verify' && `We sent a code to ${formData.email}`}
                  {authMode === '2fa' && 'Enter the code sent to your email'}
                  {authMode === 'forgot' && 'Enter your email to receive a reset code'}
                  {authMode === 'reset' && `Enter the code sent to ${formData.email}`}
                </p>
              </div>
            </div>

            {/* Form Content */}
            <div className="px-8 pb-8 space-y-4">
              {/* Error Display */}
              {(formError || authError) && (
                <div className={cn(
                  "p-3 rounded-lg text-sm flex items-center gap-2",
                  formError?.includes('sent') 
                    ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                    : "bg-red-500/20 text-red-300 border border-red-500/30"
                )}>
                  {formError?.includes('sent') ? <CheckCircle2 className="h-4 w-4" /> : <X className="h-4 w-4" />}
                  {formError || authError}
                </div>
              )}

              {/* Login Form */}
              {authMode === 'login' && (
                <form onSubmit={handleEmailLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-muted-foreground/50 text-sm">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@company.com"
                        className="pl-10 bg-foreground/50 border-white/15 text-white placeholder:text-muted-foreground focus:border-emerald-500 focus:ring-emerald-500/20"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        data-testid="login-email-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-muted-foreground/50 text-sm">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        className="pl-10 pr-10 bg-foreground/50 border-white/15 text-white placeholder:text-muted-foreground focus:border-emerald-500 focus:ring-emerald-500/20"
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        data-testid="login-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-3 text-muted-foreground hover:text-muted-foreground/50 transition-colors"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <button 
                      type="button"
                      onClick={() => { setFormError(''); setAuthMode('forgot'); }}
                      className="text-sm text-muted-foreground hover:text-emerald-400 transition-colors"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-11 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-medium shadow-lg shadow-emerald-500/25"
                    disabled={authLoading}
                    data-testid="login-submit-btn"
                  >
                    {authLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
                    Sign In
                  </Button>

                  <p className="text-center text-sm text-muted-foreground pt-2">
                    Don't have an account?{' '}
                    <button 
                      type="button"
                      onClick={() => { resetForm(); setAuthMode('register'); }}
                      className="text-emerald-400 hover:text-emerald-300 font-medium transition-colors"
                    >
                      Sign up free
                    </button>
                  </p>
                </form>
              )}

              {/* Forgot Password Form */}
              {authMode === 'forgot' && (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email" className="text-muted-foreground/50 text-sm">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="forgot-email"
                        type="email"
                        placeholder="you@company.com"
                        className="pl-10 bg-foreground/50 border-white/15 text-white placeholder:text-muted-foreground focus:border-emerald-500 focus:ring-emerald-500/20"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        data-testid="forgot-email-input"
                      />
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-11 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-medium shadow-lg shadow-emerald-500/25"
                    disabled={authLoading}
                    data-testid="forgot-submit-btn"
                  >
                    {authLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
                    Send Reset Code
                  </Button>
                  <p className="text-center text-sm text-muted-foreground pt-2">
                    Remember your password?{' '}
                    <button 
                      type="button"
                      onClick={() => { resetForm(); setAuthMode('login'); }}
                      className="text-emerald-400 hover:text-emerald-300 font-medium transition-colors"
                    >
                      Sign in
                    </button>
                  </p>
                </form>
              )}

              {/* Reset Password Form */}
              {authMode === 'reset' && (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  {/* Show dev code if email failed */}
                  {devCode && (
                    <div className="p-4 rounded-lg bg-amber-500/20 border border-amber-500/30 text-center">
                      <p className="text-amber-300 text-sm mb-2">Email delivery unavailable. Your reset code is:</p>
                      <p className="text-2xl font-mono font-bold text-white tracking-widest">{devCode}</p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="reset-code" className="text-muted-foreground/50 text-sm">Reset Code</Label>
                    <Input
                      id="reset-code"
                      type="text"
                      placeholder="000000"
                      maxLength={6}
                      className="text-center text-2xl tracking-[0.5em] font-mono h-14 bg-foreground/50 border-white/15 text-white placeholder:text-muted-foreground focus:border-emerald-500 focus:ring-emerald-500/20"
                      value={formData.verificationCode}
                      onChange={(e) => setFormData({...formData, verificationCode: e.target.value.replace(/\D/g, '')})}
                      data-testid="reset-code-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-password" className="text-muted-foreground/50 text-sm">New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="new-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Minimum 8 characters"
                        className="pl-10 pr-10 bg-foreground/50 border-white/15 text-white placeholder:text-muted-foreground focus:border-emerald-500 focus:ring-emerald-500/20"
                        value={formData.newPassword}
                        onChange={(e) => setFormData({...formData, newPassword: e.target.value})}
                        data-testid="new-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-3 text-muted-foreground hover:text-muted-foreground/50 transition-colors"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <Button 
                    type="submit"
                    className="w-full h-11 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-medium shadow-lg shadow-emerald-500/25"
                    disabled={authLoading}
                    data-testid="reset-submit-btn"
                  >
                    {authLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                    Reset Password
                  </Button>
                  <p className="text-center text-sm text-muted-foreground">
                    Didn't receive a code?{' '}
                    <button 
                      type="button"
                      onClick={handleResendForgotCode}
                      className="text-emerald-400 hover:text-emerald-300 font-medium transition-colors"
                    >
                      Resend code
                    </button>
                  </p>
                </form>
              )}

              {/* Register Form */}
              {authMode === 'register' && (
                <form onSubmit={handleEmailRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-muted-foreground/50 text-sm">Full Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="name"
                        type="text"
                        placeholder="John Doe"
                        className="pl-10 bg-foreground/50 border-white/15 text-white placeholder:text-muted-foreground focus:border-emerald-500 focus:ring-emerald-500/20"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        data-testid="register-name-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-email" className="text-muted-foreground/50 text-sm">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="reg-email"
                        type="email"
                        placeholder="you@company.com"
                        className="pl-10 bg-foreground/50 border-white/15 text-white placeholder:text-muted-foreground focus:border-emerald-500 focus:ring-emerald-500/20"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        data-testid="register-email-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-password" className="text-muted-foreground/50 text-sm">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="reg-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Minimum 8 characters"
                        className="pl-10 pr-10 bg-foreground/50 border-white/15 text-white placeholder:text-muted-foreground focus:border-emerald-500 focus:ring-emerald-500/20"
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        data-testid="register-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-3 text-muted-foreground hover:text-muted-foreground/50 transition-colors"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-11 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-medium shadow-lg shadow-emerald-500/25"
                    disabled={authLoading}
                    data-testid="register-submit-btn"
                  >
                    {authLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowRight className="h-4 w-4 mr-2" />}
                    Create Account
                  </Button>

                  <p className="text-center text-sm text-muted-foreground pt-2">
                    Already have an account?{' '}
                    <button 
                      type="button"
                      onClick={() => { resetForm(); setAuthMode('login'); }}
                      className="text-emerald-400 hover:text-emerald-300 font-medium transition-colors"
                    >
                      Sign in
                    </button>
                  </p>
                </form>
              )}

              {/* Verification Form */}
              {authMode === 'verify' && (
                <form onSubmit={handleVerifyCode} className="space-y-4">
                  {/* Show dev code if email failed */}
                  {devCode && (
                    <div className="p-4 rounded-lg bg-amber-500/20 border border-amber-500/30 text-center">
                      <p className="text-amber-300 text-sm mb-2">Email delivery unavailable. Your verification code is:</p>
                      <p className="text-2xl font-mono font-bold text-white tracking-widest">{devCode}</p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="code" className="text-muted-foreground/50 text-sm">Verification Code</Label>
                    <Input
                      id="code"
                      type="text"
                      placeholder="000000"
                      maxLength={6}
                      className="text-center text-2xl tracking-[0.5em] font-mono h-14 bg-foreground/50 border-white/15 text-white placeholder:text-muted-foreground focus:border-emerald-500 focus:ring-emerald-500/20"
                      value={formData.verificationCode}
                      onChange={(e) => setFormData({...formData, verificationCode: e.target.value.replace(/\D/g, '')})}
                      data-testid="verify-code-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-11 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-medium shadow-lg shadow-emerald-500/25"
                    disabled={authLoading}
                    data-testid="verify-submit-btn"
                  >
                    {authLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                    Verify & Continue
                  </Button>
                  {!devCode && (
                    <p className="text-center text-sm text-muted-foreground">
                      Didn't receive a code?{' '}
                      <button 
                        type="button"
                        onClick={handleResendCode}
                        className="text-emerald-400 hover:text-emerald-300 font-medium transition-colors"
                      >
                        Resend code
                      </button>
                    </p>
                  )}
                </form>
              )}

              {/* 2FA Form */}
              {authMode === '2fa' && (
                <form onSubmit={handle2FAVerify} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="2fa-code" className="text-muted-foreground/50 text-sm">Security Code</Label>
                    <Input
                      id="2fa-code"
                      type="text"
                      placeholder="000000"
                      maxLength={6}
                      className="text-center text-2xl tracking-[0.5em] font-mono h-14 bg-foreground/50 border-white/15 text-white placeholder:text-muted-foreground focus:border-emerald-500 focus:ring-emerald-500/20"
                      value={formData.verificationCode}
                      onChange={(e) => setFormData({...formData, verificationCode: e.target.value.replace(/\D/g, '')})}
                      data-testid="2fa-code-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-11 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-medium shadow-lg shadow-emerald-500/25"
                    disabled={authLoading}
                    data-testid="2fa-submit-btn"
                  >
                    {authLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Shield className="h-4 w-4 mr-2" />}
                    Verify
                  </Button>
                </form>
              )}

              {/* Footer */}
              <p className="text-center text-xs text-muted-foreground pt-4 border-t border-white/10">
                By continuing, you agree to CountBucks'{' '}
                <a href="#" className="text-muted-foreground hover:text-emerald-400">Terms of Service</a>
                {' '}and{' '}
                <a href="#" className="text-muted-foreground hover:text-emerald-400">Privacy Policy</a>
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
